import { FC, Fragment, useState } from 'react';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import {
	DeleteOutlined,
	PlusOutlined,
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	CreateCustomerForm,
	CustomerCompanyManagementInformationForm,
	CustomerCompanyShareholderInformationForm,
	useFRMCustomerPage,
} from './CustomerPage';
import { useModals } from '@/components/modals/ModalProvider';
import dayjs from 'dayjs';
import { useForm } from '@/libs/antd/form';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule, { rules } from '@/libs/antd/rules';
import {
	useSelectAddress,
	useSelectCitizenship,
	useSelectCountry,
	useSelectGeneralStatus,
	useSelectIdentityType,
	useSelectManagementPosition,
} from '@/libs/fleet-api/hooks/master-data';
import { ShareholderType } from '@/libs/fleet-api/enums';
import { inputKitas, inputKtp, inputPassport } from '@/libs/antd/input';
import { CustomerManagement, FileData } from '@/libs/fleet-api/services/types';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { inputRtRw } from '@/libs/antd/input-number';

const FRMCustomerLegalManager: FC<{ onSubmit: () => void }> = ({
	onSubmit,
}) => {
	const { modal, openManagementModal } = useModals();
	const { customerForm, managerForm, validationStatus } = useFRMCustomerPage();

	return (
		<Fragment>
			<Form.Item shouldUpdate>
				{({ getFieldValue }) => {
					const dataSource =
						(getFieldValue(
							customerForm.fieldName('customerCompanyManagementInformation'),
						) as CreateCustomerForm['customerCompanyManagementInformation']) ??
						[];

					return (
						<Fragment>
							<div style={{ textAlign: 'right', marginBottom: 10 }}>
								<Button
									type="link"
									icon={<PlusOutlined />}
									onClick={() => {
										managerForm.setModalState({ type: 'create' });
									}}
								>
									Tambah Kepengurusan
								</Button>
							</div>
							<Table
								dataSource={dataSource.map((data, key) => {
									return { ...data, key };
								})}
								onRow={(data) => {
									if (
										!data.name ||
										!data.identityCardType ||
										!data.identityFile ||
										!data.address ||
										!data.rt ||
										!data.rw ||
										!data.postal
									) {
										return {
											style: {
												background: '#fff3f399',
											},
										};
									}

									return {};
								}}
								columns={[
									{
										title: 'Action',
										width: 1,
										align: 'center',
										colSpan: 3,
										render: (_, record) => (
											<a
												onClick={(e) => {
													e.preventDefault();
													openManagementModal(
														record as unknown as CustomerManagement,
													);
												}}
											>
												<FileSearchOutlined />
											</a>
										),
									},
									{
										width: 1,
										align: 'center',
										colSpan: 0,
										render: (_, record, index) => (
											<a
												style={{ display: 'block' }}
												onClick={(e) => {
													e.preventDefault();
													managerForm.setModalState({
														type: 'edit',
														index,
													});
													managerForm.form?.setFieldsValue(record);
												}}
											>
												<EditOutlined />
											</a>
										),
									},
									{
										width: 1,
										align: 'center',
										colSpan: 0,
										render: (_, record, index) => (
											<a
												style={{ display: 'block' }}
												onClick={async (e) => {
													e.preventDefault();
													const isConfirmed = await modal.confirm({
														title: 'Hapus data pengurus?',
													});

													if (!isConfirmed) return;
													customerForm.form.setFieldValue(
														customerForm.fieldName(
															'customerCompanyManagementInformation',
														),
														customerForm.value?.customerCompanyManagementInformation?.filter(
															(_, dataIndex) => dataIndex !== index,
														),
													);
													// console.log(
													// 	'management',
													// 	customerForm.value
													// 		?.customerCompanyManagementInformation,
													// );
													// console.log(
													// 	'shareholder',
													// 	customerForm.value
													// 		?.customerCompanyShareholderInformation,
													// );
													customerForm.form.setFieldValue(
														customerForm.fieldName(
															'customerCompanyShareholderInformation',
														),
														customerForm.value?.customerCompanyShareholderInformation?.filter(
															(shareholder) => {
																return (
																	!shareholder.isManagement ||
																	shareholder.id !== record.tempId
																);
															},
														),
													);
												}}
											>
												<DeleteOutlined />
											</a>
										),
									},
									{
										title: 'Nama',
										dataIndex: ['name'],
									},
									// {
									// 	title: 'id',
									// 	dataIndex: ['id'],
									// },
									// {
									// 	title: 'tempId',
									// 	dataIndex: ['tempId'],
									// },
									{
										title: 'Kewarganegaraan',
										dataIndex: ['citizenship', 'value'],
									},
									{
										title: 'Nationality',
										dataIndex: ['nationality', 'name'],
									},
									{
										title: 'Jenis Identitas',
										dataIndex: ['identityCardType', 'value'],
									},
									{
										title: 'No. ID',
										dataIndex: 'identityNumber',
									},
									{
										title: 'Tanggal Lahir',
										dataIndex: ['dateOfBirth'],
									},
									{
										title: 'Jabatan',
										dataIndex: ['position', 'value'],
									},
									{
										title: 'Alamat Perusahaan',
										dataIndex: 'address',
									},
									{
										title: 'Kode Pos',
										dataIndex: ['postal', 'postalCode'],
									},
									{
										title: 'Kota',
										dataIndex: ['postal', 'cityName'],
									},
									{
										title: 'Kecamatan',
										dataIndex: ['postal', 'districtName'],
									},
									{
										title: 'Kelurahan',
										dataIndex: ['postal', 'locationName'],
									},
									{
										title: 'RT/RW',
										render: (record) => `${record.rt}/${record.rw}`,
									},
									{
										title: 'No. Telepon',
										dataIndex: ['phoneNumber'],
									},
									{
										title: 'Status',
										render: (record) =>
											record.status ? 'Aktif' : 'Tidak Aktif',
									},
								]}
								scroll={{ x: 'max-content' }}
								pagination={false}
								bordered
							/>
						</Fragment>
					);
				}}
			</Form.Item>

			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 60,
				}}
				disabled={!validationStatus.management}
				onClick={() => onSubmit()}
			>
				NEXT
			</Button>
		</Fragment>
	);
};

export default FRMCustomerLegalManager;

export const useFRMCustomerLegalManagerForm = () => {
	const { form, value, fieldName } =
		useForm<CustomerCompanyManagementInformationForm>();

	const [modalState, setModalState] = useState<
		{ type: 'create' } | { type: 'edit'; index: number } | null
	>(null);

	return {
		form,
		value,
		fieldName,
		modalState,
		setModalState,
	};
};

export const FRMCustomerLegalManagerForm: FC<
	ReturnType<typeof useFRMCustomerLegalManagerForm>
> = (props) => {
	const {
		customerForm,
		customerDetail,
		totalShareholding,
		isEdit,
		customerId,
	} = useFRMCustomerPage();
	const { form, value, fieldName, modalState, setModalState } = props;

	const selectCitizenship = useSelectCitizenship();
	const selectCountry = useSelectCountry();
	const selectPosition = useSelectManagementPosition();
	const selectAddress = useSelectAddress();
	const selectStatus = useSelectGeneralStatus();
	const selectIdentityCardType = useSelectIdentityType(
		ShareholderType.Perorangan,
	);

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const isShareholderExist =
		customerForm.value?.customerCompanyShareholderInformation?.some(
			(data) =>
				data.isManagement &&
				(data.id === value?.id || data.tempId === value?.tempId),
		);

	const enableShareholdingControl =
		(modalState?.type === 'edit' && isShareholderExist) ||
		(modalState?.type === 'edit' && totalShareholding <= 100) ||
		(modalState?.type === 'create' && totalShareholding < 100);

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			labelCol={{ span: 10 }}
			wrapperCol={{ span: 14 }}
			onFinish={(value) => {
				console.log(value);
				const existings =
					customerForm.value?.customerCompanyManagementInformation ?? [];

				const tempId = value.tempId ?? Date.now();
				const updated =
					modalState?.type === 'edit'
						? existings.map((doc, docIndex) =>
								modalState.index === docIndex ? { ...value, tempId } : doc,
							)
						: [...existings, { ...value, tempId }];

				customerForm.form.setFieldValue(
					customerForm.fieldName('customerCompanyManagementInformation'),
					updated,
				);

				if (!value.isShareholder && isShareholderExist) {
					customerForm.form.setFieldValue(
						customerForm.fieldName('customerCompanyShareholderInformation'),
						customerForm.value!.customerCompanyShareholderInformation!.filter(
							(data) => {
								return data.id !== value.id || data.tempId !== value.tempId;
							},
						),
					);
				}

				if (value.isShareholder) {
					const shareholders =
						customerForm.value?.customerCompanyShareholderInformation ?? [];

					const newShareholder: CustomerCompanyShareholderInformationForm = {
						id: value?.id ?? tempId,
						tempId,
						customerId: customerDetail?.customerInformation.id,
						name: value?.name ?? '',
						citizenship: value?.citizenship,
						nationality: value?.nationality,
						identityCardType: value?.identityCardType,
						identityNumber: value?.identityNumber,
						address: value?.address ?? '',
						rt: value?.rt ?? '',
						rw: value?.rw ?? '',
						postal: value?.postal,
						phoneNumber: value?.phoneNumber ?? '',
						status: value?.status,
						identityFile: value?.identityFile,
						isManagement: true,
						shareholderType: {
							id: 1,
							value: 'Perorangan',
						},
						shareholding: value?.shareholding ?? 0,
					};

					const updatedShareholders = isShareholderExist
						? shareholders.map((data) => {
								return (modalState?.type === 'edit' &&
									data.id === value.id &&
									value.id !== undefined) ||
									data.tempId === value.tempId
									? newShareholder
									: data;
							})
						: [...shareholders, newShareholder];

					customerForm.form.setFieldValue(
						customerForm.fieldName('customerCompanyShareholderInformation'),
						updatedShareholders,
					);
				}

				setModalState(null);
			}}
		>
			<FleetModal
				open={Boolean(modalState)}
				width={940}
				closeIcon={false}
				keyboard={false}
				maskClosable={false}
				buttonPosition="end"
				okButtonProps={{
					disabled: uploadMutation.isPending,
				}}
				onOk={() => form?.submit()}
				onCancel={() => setModalState(null)}
				afterClose={() => form.resetFields()}
			>
				<FleetTitle>Tambah Pengurus</FleetTitle>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Form.Item
					name={fieldName('tempId')}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('name')}
							label="Nama"
							rules={
								isEdit && customerDetail?.customerInformation.source === 'Estar'
									? [rule.required()]
									: [rule.required(), rule.charOnly()]
							}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('citizenship')}
							label="Kewarganegaraan"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select
								{...selectCitizenship}
								onSelect={(value, option) => {
									selectCitizenship.onSelect!(value, option);
									form.resetFields([
										fieldName('nationality'),
										fieldName('identityCardType'),
									]);
								}}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('nationality')}
							label="Nationality"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.name,
									},
								};
							}}
						>
							<Select
								{...selectCountry}
								options={selectCountry.options?.filter((option) => {
									return value?.citizenship?.id === 1
										? option.value === 1
										: option.value !== 1;
								})}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('identityCardType')}
							label="Jenis Identitas"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select
								{...selectIdentityCardType}
								options={
									value?.citizenship?.id === 2
										? selectIdentityCardType.options?.filter(
												(option) => option.value !== 1,
											)
										: selectIdentityCardType.options
								}
								onSelect={(val, option) => {
									selectIdentityCardType.onSelect!(val, option);
								}}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						{value?.identityCardType?.id === 1 && (
							<Form.Item
								name={fieldName('identityNumber')}
								label="No. ID"
								rules={rules.ktp}
							>
								<Input {...inputKtp} />
							</Form.Item>
						)}
						{value?.identityCardType?.id === 2 && (
							<Form.Item
								name={fieldName('identityNumber')}
								label="No. ID"
								rules={rules.passport}
							>
								<Input {...inputPassport} />
							</Form.Item>
						)}
						{value?.identityCardType?.id === 3 && (
							<Form.Item
								name={fieldName('identityNumber')}
								label="No. ID"
								rules={rules.kitas}
							>
								<Input {...inputKitas} />
							</Form.Item>
						)}
					</Col>
					<Col span={12}>
						<Form.Item
							label="Upload Identitas"
							name={fieldName('identityFile')}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".pdf,.jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName('identityFile'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = customerId
										? `customer/${customerId}/management-document`
										: 'customer/temp/management-document';

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(fieldName('identityFile'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('identityFile'), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('dateOfBirth')}
							label="Tanggal Lahir"
							rules={[rule.required()]}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
						>
							<DatePicker
								disabledDate={(now) => now > dayjs().subtract(1, 'day')}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('position')}
							label="Jabatan"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select {...selectPosition} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('address')}
							label="Alamat"
							rules={[rule.required()]}
						>
							<Input.TextArea rows={3} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="RT/RW"
							required
						>
							<Row
								wrap={false}
								align="middle"
								gutter={8}
							>
								<Col flex="auto">
									<Form.Item
										name={fieldName('rt')}
										noStyle
										rules={rules.rt}
									>
										<Input {...inputRtRw} />
									</Form.Item>
								</Col>
								<Col flex="none">/</Col>
								<Col flex="auto">
									<Form.Item
										name={fieldName('rw')}
										noStyle
										rules={rules.rw}
									>
										<Input {...inputRtRw} />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('postal')}
							label="Kode POS"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.postalId,
										label: value.postalCode,
									},
								};
							}}
						>
							<Select {...selectAddress} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Kelurahan"
							shouldUpdate
						>
							<Input
								disabled
								value={value?.postal?.locationName}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Kecamatan"
							shouldUpdate
						>
							<Input
								disabled
								value={value?.postal?.districtName}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Kota"
							shouldUpdate
						>
							<Input
								disabled
								value={value?.postal?.cityName}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('phoneNumber')}
							label="No. Telepon"
							validateTrigger={['onBlur', 'onFocus']}
							rules={[rule.strNumber(), rule.phoneNumber()]}
						>
							<Input maxLength={13} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('status')}
							label="Status"
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select {...selectStatus} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('isShareholder')}
							valuePropName="checked"
							wrapperCol={{ span: 24 }}
						>
							<Checkbox disabled={!enableShareholdingControl}>
								<div style={{ fontSize: 14, paddingLeft: 10 }}>
									Checklist kotak disamping apabila{' '}
									<strong style={{ color: '#1557BC' }}>Pengurus</strong> juga
									merupakan{' '}
									<strong style={{ color: '#1557BC' }}>Pemegang Saham</strong>
								</div>
							</Checkbox>
						</Form.Item>
					</Col>
					<Col span={12}>
						{value?.isShareholder && (
							<Form.Item
								name={fieldName('shareholding')}
								label="Kepemilikan Saham"
								rules={[rule.required()]}
							>
								<InputNumber
									disabled={!enableShareholdingControl}
									min={0}
									suffix="%"
								/>
							</Form.Item>
						)}
					</Col>
				</Row>
			</FleetModal>
		</Form>
	);
};
